import { ProductDecorationMethod } from '@codezo/graphql';
import { cn } from './cn';
import {
	SPEC_FIELDS,
	decorationMethodLabel,
	type SpecColorLike,
	type SpecDecorationLike,
	type SpecDimensionsLike,
	type SpecificationsLike,
} from './spec-fields';

export interface SpecificationRow {
	key: string;
	label: string;
	value: React.ReactNode;
}

export interface SpecificationsSource {
	specifications?: SpecificationsLike | null;
	/** The product's own overall dimensions — not part of the spec object. */
	dimensions?: SpecDimensionsLike | null;
	/** The product's own weight, in kilograms. */
	weight?: number | null;
}

const joinParts = (parts: (string | null | undefined | false)[]) =>
	parts.filter(Boolean).join(' · ');

/**
 * Renders only the axes that carry a value, so a flat product reads
 * "38 × 42 cm" rather than "38 × 42 × 0 cm".
 */
const formatDimensions = (dims: SpecDimensionsLike | null | undefined) => {
	if (!dims) return null;
	const axes = [dims.length, dims.width, dims.height].filter(
		(axis): axis is number => axis != null && axis > 0
	);
	if (axes.length === 0) return null;
	return `${axes.join(' × ')} ${dims.unit || 'cm'}`;
};

const formatWeight = (weight: number | null | undefined) =>
	weight != null && weight > 0 ? `${weight} kg` : null;

/** A swatch only renders when the code is something CSS can actually paint. */
const isHex = (code: string | null | undefined) =>
	!!code && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(code);

function ColorChips({ colors }: { colors: readonly SpecColorLike[] }) {
	return (
		<div className="flex flex-wrap justify-end gap-x-3 gap-y-1.5">
			{colors.map((color, i) => (
				<span key={i} className="flex items-center gap-1.5">
					{isHex(color.code) && (
						<span
							className="h-3 w-3 shrink-0 rounded-full border border-line-2"
							style={{ backgroundColor: color.code! }}
						/>
					)}
					<span className="text-text-1">
						{joinParts([
							color.name,
							!color.name && color.code,
							color.pantone,
							color.appliesTo,
							color.finish,
						]) || 'Colour'}
					</span>
					{color.isPrimary && <span className="text-2xs text-text-4">primary</span>}
				</span>
			))}
		</div>
	);
}

const joinAxes = (...axes: (number | null | undefined)[]) =>
	axes.filter((axis): axis is number => axis != null && axis > 0).join(' × ');

const describeDecoration = (decoration: SpecDecorationLike) => {
	const method =
		decoration.method === ProductDecorationMethod.Other && decoration.methodNote
			? decoration.methodNote
			: decorationMethodLabel(decoration.method);
	const area =
		decoration.area && (decoration.area.width || decoration.area.height)
			? `${joinAxes(decoration.area.width, decoration.area.height)} ${decoration.area.unit || 'cm'}`
			: null;

	return joinParts([
		method,
		decoration.colorCount != null &&
			`${decoration.colorCount} ${decoration.colorCount === 1 ? 'colour' : 'colours'}`,
		area,
		decoration.notes,
	]);
};

/**
 * Turns a stored specification (plus the product's own dimensions and weight)
 * into the flat label/value rows the specification table renders.
 */
export function buildSpecificationRows({
	specifications,
	dimensions,
	weight,
}: SpecificationsSource): SpecificationRow[] {
	const rows: SpecificationRow[] = [];

	const productDims = formatDimensions(dimensions);
	if (productDims) rows.push({ key: 'dimensions', label: 'Dimensions', value: productDims });

	const productWeight = formatWeight(weight);
	if (productWeight) rows.push({ key: 'weight', label: 'Unit weight', value: productWeight });

	const spec = specifications;
	if (!spec) return rows;

	for (const def of SPEC_FIELDS) {
		let value: React.ReactNode = null;

		switch (def.key) {
			case 'material':
				value = spec.material;
				break;
			case 'secondaryMaterials':
				value = spec.secondaryMaterials?.join(' · ');
				break;
			case 'finish':
				value = spec.finish;
				break;
			case 'textureDescription':
				value = spec.textureDescription;
				break;
			case 'features':
				value = spec.features?.join(' · ');
				break;
			case 'capacity':
				value = spec.capacity && `${spec.capacity.value} ${spec.capacity.unit}`;
				break;
			case 'packaging.type':
				value = spec.packaging?.type;
				break;
			case 'packaging.dimensions':
				value = formatDimensions(spec.packaging?.dimensions);
				break;
			case 'packaging.weight':
				value =
					spec.packaging?.weight != null &&
					`${spec.packaging.weight} ${spec.packaging.weightUnit || 'g'}`;
				break;
			case 'packaging.notes':
				value = spec.packaging?.notes;
				break;
		}

		if (value === null || value === undefined || value === false || value === '') continue;
		rows.push({ key: def.key, label: def.label, value });
	}

	if (spec.colors && spec.colors.length > 0) {
		rows.push({
			key: 'colors',
			label: spec.colors.length === 1 ? 'Colour' : 'Colours',
			value: <ColorChips colors={spec.colors} />,
		});
	}

	for (const decoration of [...(spec.decorations ?? [])].sort(
		(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
	)) {
		rows.push({
			key: `decoration-${decoration.id}`,
			label: decoration.placement ? `Print · ${decoration.placement}` : 'Print',
			value: describeDecoration(decoration),
		});
	}

	for (const entry of [...(spec.custom ?? [])].sort(
		(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
	)) {
		rows.push({
			key: `custom-${entry.id}`,
			label: entry.label,
			value: entry.unit ? `${entry.value} ${entry.unit}` : entry.value,
		});
	}

	return rows;
}

/**
 * Read-only specification table: two columns on wide viewports, one on narrow,
 * with the trailing row's rule trimmed so the block closes cleanly.
 */
export function SpecificationsView({ rows }: { rows: SpecificationRow[] }) {
	/* Index the visually-last row starts at, so those rows lose their rule. */
	const lastRowStart = rows.length - (rows.length % 2 === 0 ? 2 : 1);
	/* An odd count leaves the final cell alone in the left column. */
	const lastIsLone = rows.length % 2 === 1;

	return (
		<dl className="grid grid-cols-1 sm:grid-cols-2">
			{rows.map((row, i) => {
				/* Included conditionally rather than overridden: `odd:` carries
				 * more specificity than a plain utility, so a later class
				 * would not win. */
				const columnRule = !(lastIsLone && i === rows.length - 1);

				return (
					<div
						key={row.key}
						className={cn(
							'flex items-start justify-between gap-4 border-b border-line-1 px-4 py-2.5 text-sm',
							i === rows.length - 1 && 'border-b-0',
							columnRule && 'sm:odd:border-r sm:odd:border-r-line-1',
							i >= lastRowStart && 'sm:border-b-0'
						)}
					>
						<dt className="shrink-0 text-text-3">{row.label}</dt>
						<dd className="min-w-0 text-right font-medium text-text-1">{row.value}</dd>
					</div>
				);
			})}
		</dl>
	);
}
